import * as z from "zod";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Button, Input, Select } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { faEye } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  useCreateScene,
  useGetSceneDetail,
  useUpdateScene,
} from "../../api/scenes";
import Head from "../Head";
import { statusOptions } from "../../data";
import { ErrorLabel, Label } from "../atomic";
import { useRHForm, ControlledField } from "../form";
import styleSceneCreate from "./SceneCreate.module.css";
import { useGetCategoriesDropdown } from "../../api/categories";
import { useGetUnityScenesDropdownForScene } from "../../api/unityScene";
import { useGetWebUrlsDropdown } from "../../api/webUrls";
import { useGetToursDropdown } from "../../api/tours";
import { CONFIG } from "../../config";

const EMBED_TO_TYPE = {
  unity_scene: "unityScene",
  web_url: "webUrl",
  "360_tours": "360Tour",
};

const TYPE_TO_EMBED = {
  unityScene: "unity_scene",
  webUrl: "web_url",
  "360Tour": "360_tours",
};

const visibilityOptions = [
  { value: "Public", label: "PUBLIC" },
  { value: "Private", label: "PRIVATE" },
];

const relatedId = value => {
  if (value == null || value === "") return undefined;
  if (typeof value === "object") return value.id;
  return value;
};

const SceneCreate = () => {
  const { id, public_id } = useParams();
  const navigate = useNavigate();
  const [embedType, setEmbedType] = useState("unity_scene");
  const { data: webUrls = [] } = useGetWebUrlsDropdown();
  const { data: tours = [] } = useGetToursDropdown();
  const {
    mutateAsync: mutateAsyncCreate,
    isLoading: isLoadingCreate,
    error: errorCreate,
  } = useCreateScene();
  const {
    mutateAsync: mutateAsyncUpdate,
    isLoading: isLoadingUpdate,
    error: errorUpdate,
  } = useUpdateScene(id);
  const { data: scene } = useGetSceneDetail(public_id);
  const { data: categories } = useGetCategoriesDropdown();
  const { data: unityScenes } = useGetUnityScenesDropdownForScene(id);

  const categoriesOptions = categories?.map((category) => ({
    value: category?.id,
    label: category?.name,
  }));

  const unityScenesOptions = unityScenes?.map((unityScene) => ({
    value: unityScene?.id,
    label: unityScene?.name,
  }));

  const webUrlOptions = (Array.isArray(webUrls) ? webUrls : []).map((webUrl) => ({
    value: webUrl?.id,
    label: webUrl?.name || webUrl?.url,
  }));

  const tourOptions = (Array.isArray(tours) ? tours : []).map((tour) => ({
    value: tour?.id,
    label: tour?.title || tour?.name,
  }));

  const {
    Form,
    methods: {
      register,
      control,
      setValue,
      watch,
      formState: { dirtyFields, errors },
    },
  } = useRHForm({
    initialValues: {
      status: "PUBLISHED",
      visibility: "Public",
      order: 0,
    },
    schema: z.object({
      title: z.string({ required_error: "Title is required." }),
      slug: z.string({ required_error: "Slug is required." }),
      image: z.any().refine((val) => val.length > 0, "Image is required."),
      web_url: z.any().optional(),
      tour_360: z.any().optional(),
      unity_scene: z.number().optional(),
      description: z.string({ required_error: "Description is required" }),
      interactions: z.number().array().optional(),
      product_categories: z.number().array().optional(),
      scene_categories: z.number().array().optional(),
      status: z.string({ required_error: "Status is required." }),
      visibility: z.string().optional(),
      order: z.any(),
    }),
  });

  const handleSubmit = (data) => {
    const formData = new FormData();
    if (data?.title) {
      formData.append("title", data?.title);
    }
    if (data?.slug) {
      formData.append("slug", data?.slug);
    }
    if (data?.image?.[0]?.name) {
      formData.append("image", data?.image?.[0]);
    }
    if (data?.description) {
      formData.append("description", data?.description);
    }
    const apiType = EMBED_TO_TYPE[embedType] || "unityScene";
    formData.append("type", apiType);
    if (apiType === "unityScene") {
      formData.append("unity_scene", data?.unity_scene ?? "");
    } else if (apiType === "webUrl") {
      formData.append("web_url", data?.web_url ?? "");
    } else if (apiType === "360Tour") {
      formData.append("tour_360", data?.tour_360 ?? "");
    }
    formData.append("visibility", data?.visibility || "Public");
    formData.append("order", String(parseInt(data?.order, 10) || 0));
    if (data?.interactions) {
      formData.append("interactions", data?.interactions);
    }
    if (data?.product_categories) {
      formData.append("product_categories", data?.product_categories);
    }
    if (data?.scene_categories) {
      formData.append("scene_categories", data?.scene_categories);
    }
    if (data?.status) {
      formData.append("status", data?.status);
    }

    if (id) {
      mutateAsyncUpdate(formData)
        .then((res) => {
          if (res?.data) {
            toast.success(res?.data?.message);
            navigate("/web-twinprocms/scene");
          }
        })
        .catch((error) => {
          toast.error(error?.message);
        });
      return;
    }

    mutateAsyncCreate(formData)
      .then((res) => {
        if (res?.data) {
          toast.success(res?.data?.message);
          navigate("/web-twinprocms/scene");
        }
      })
      .catch((error) => {
        toast.error(error?.message);
      });
  };
  const watchTitle = watch("title");

  useEffect(() => {
    if (dirtyFields?.title) {
      let slugValue;
      slugValue = watchTitle
        ?.toString()
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.trim()
        ?.replace(/[^a-z0-9 -]/g, "")
        ?.replace(/\s+/g, "-");
      setValue("slug", slugValue);
    }
  }, [watchTitle, setValue, dirtyFields]);

  useEffect(() => {
    if (scene) {
      const sceneDetail = {
        ...scene,
        unity_scene: relatedId(scene?.unity_scene) ?? scene?.Unity_Scene?.id,
        web_url: relatedId(scene?.web_url),
        tour_360: relatedId(scene?.tour_360),
        visibility: scene?.visibility || "Public",
        order: scene?.order ?? 0,
        interactions: scene?.interactions?.map((element) => element?.id),
        product_categories: scene?.product_categories?.map(
          (element) => element?.id
        ),
        scene_categories: scene?.scene_categories?.map(
          (element) => element?.id
        ),
      };
      const formFields = new Set([
        "title",
        "slug",
        "image",
        "web_url",
        "tour_360",
        "unity_scene",
        "description",
        "interactions",
        "product_categories",
        "scene_categories",
        "status",
        "visibility",
        "order",
      ]);
      Object.entries(sceneDetail).forEach(([key, value]) => {
        if (!formFields.has(key)) return;
        setValue(key, value, {
          shouldValidate: true,
          shouldDirty: false,
          shouldTouch: false,
        });
      });

      if (scene?.type && TYPE_TO_EMBED[scene.type]) {
        setEmbedType(TYPE_TO_EMBED[scene.type]);
      } else if (scene?.tour_360 || scene?.tour_url) {
        setEmbedType("360_tours");
      } else if (scene?.web_url && !scene?.unity_scene && !scene?.Unity_Scene) {
        setEmbedType("web_url");
      } else {
        setEmbedType("unity_scene");
      }
    }
  }, [scene, setValue]);

  const handleEmbedTypeChange = (e) => {
    const nextType = e.target.value;
    setEmbedType(nextType);
    if (nextType !== "unity_scene") setValue("unity_scene", undefined);
    if (nextType !== "web_url") setValue("web_url", undefined);
    if (nextType !== "360_tours") setValue("tour_360", undefined);
  };

  const handleDownload = async (filePath) => {
    try {
      const url = `${CONFIG.S3_BASE_URI}/${filePath}`;
  
      const response = await fetch(url);
  
      if (!response.ok) {
        throw new Error("Failed to download file");
      }
  
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
  
      const fileName = filePath.split("/").pop();
  
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
  
      document.body.appendChild(link);
      link.click();
  
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  return (
    <main className={styleSceneCreate["create-scene-main-wrapper"]}>
      <Head title="Scene" />
      <div className={styleSceneCreate["title-container"]}>
        <h1>Scene</h1>
      </div>
      <section className={styleSceneCreate["form-wrapper"]}>
        <Form onSubmit={handleSubmit}>
          <ControlledField
            required
            name="title"
            label="Title"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(errorCreate || errorUpdate)?.response?.data?.title?.toString()}
          </ErrorLabel>
          <ControlledField
            required
            name="slug"
            label="Slug"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(errorCreate || errorUpdate)?.response?.data?.slug?.toString()}
          </ErrorLabel>
          <Label required>Image</Label>
          <p>
            Supported image extensions: PNG, SVG, JPG, JPEG Preferred Dimension:
            1920 x 1080 (W x H)
          </p>
          {scene?.image && (
            <>
              <span>Currently:</span>
              <a
  href="#"
  className="text-blue-500 hover:underline"
  onClick={(e) => {
    e.preventDefault();
    handleDownload(scene?.image);
  }}
>
  {`${CONFIG.S3_BASE_URI}/${scene?.image}`}
</a>
              <span>Change: </span>
            </>
          )}
          <label className={styleSceneCreate["file-label"]}>
            <input type="file" {...register("image")} />
          </label>
          <ErrorLabel>{errors?.image?.message}</ErrorLabel>
          <ErrorLabel>
            {(errorCreate || errorUpdate)?.response?.data?.image?.toString()}
          </ErrorLabel>

          <Label>Scene Type</Label>
          <ul className={styleSceneCreate["scene-embed-type"]}>
            <li>
              <label htmlFor="3d-model">
                <input
                  type="radio"
                  value="unity_scene"
                  name="embedType"
                  onChange={handleEmbedTypeChange}
                  checked={embedType === "unity_scene"}
                />
                Unity Scene
              </label>
            </li>
            <li>
              <label htmlFor="image">
                <input
                  type="radio"
                  value="web_url"
                  name="embedType"
                  onChange={handleEmbedTypeChange}
                  checked={embedType === "web_url"}
                />
                Web URL
              </label>
            </li>
            <li>
              <label htmlFor="360-tours">
                <input
                  type="radio"
                  value="360_tours"
                  name="embedType"
                  onChange={handleEmbedTypeChange}
                  checked={embedType === "360_tours"}
                />
                360 Tours
              </label>
            </li>
          </ul>
          {embedType === "unity_scene" && (
            <>
              <ControlledField
                name="unity_scene"
                label="Unity Scene"
                control={control}
                Component={Select}
                componentProps={{
                  options: unityScenesOptions,
                  placeholder: "Select unity scene",
                  allowClear: true,
                  showSearch: true,
                  filterOption: (inputValue, option) =>
                    option.label
                      .toLowerCase()
                      .indexOf(inputValue.toLowerCase()) >= 0,
                }}
              />
              <ErrorLabel>
                {(
                  errorCreate || errorUpdate
                )?.response?.data?.unity_scene?.toString()}
              </ErrorLabel>
            </>
          )}
          {embedType === "web_url" && (
            <>
              <ControlledField
                name="web_url"
                label="Web Url"
                control={control}
                Component={Select}
                componentProps={{
                  options: webUrlOptions,
                  placeholder: "Select a Web URL",
                  allowClear: true,
                  showSearch: true,
                  filterOption: (inputValue, option) =>
                    option.label
                      .toLowerCase()
                      .indexOf(inputValue.toLowerCase()) >= 0,
                }}
                errors={errors}
              />
              <ErrorLabel>
                {(
                  errorCreate || errorUpdate
                )?.response?.data?.web_url?.toString()}
              </ErrorLabel>
            </>
          )}
          {embedType === "360_tours" && (
            <>
              <ControlledField
                name="tour_360"
                label="360 Tours"
                control={control}
                Component={Select}
                componentProps={{
                  options: tourOptions,
                  placeholder: "Select a 360 tour",
                  allowClear: true,
                  showSearch: true,
                  filterOption: (inputValue, option) =>
                    option.label
                      .toLowerCase()
                      .indexOf(inputValue.toLowerCase()) >= 0,
                }}
                errors={errors}
              />
              <ErrorLabel>
                {(
                  errorCreate || errorUpdate
                )?.response?.data?.tour_360?.toString()}
              </ErrorLabel>
            </>
          )}
          <ControlledField
            required
            name="description"
            label="Description"
            control={control}
            Component={Input}
            errors={errors}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.description?.toString()}
          </ErrorLabel>

          <ControlledField
            name="scene_categories"
            label="Categories"
            control={control}
            Component={Select}
            componentProps={{
              mode: "multiple",
              options: categoriesOptions,
              placeholder: "Select Category",
              allowClear: true,
              showSearch: true,
              filterOption: (inputValue, option) =>
                option.label.toLowerCase().indexOf(inputValue.toLowerCase()) >=
                0,
            }}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.scene_categories?.toString()}
          </ErrorLabel>
          <ControlledField
            required
            name="status"
            label="Status"
            control={control}
            Component={Select}
            componentProps={{
              options: statusOptions,
              placeholder: "Select status",
            }}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.status?.toString()}
          </ErrorLabel>
          <ControlledField
            name="visibility"
            label="Visibility"
            control={control}
            Component={Select}
            componentProps={{
              options: visibilityOptions,
              placeholder: "Select visibility",
            }}
          />
          <ErrorLabel>
            {(
              errorCreate || errorUpdate
            )?.response?.data?.visibility?.toString()}
          </ErrorLabel>
          <ControlledField
            name="order"
            label="Order"
            control={control}
            Component={Input}
            errors={errors}
            componentProps={{
              type: "number",
            }}
          />
          <ErrorLabel>
            {(errorCreate || errorUpdate)?.response?.data?.order?.toString()}
          </ErrorLabel>
          <div className={styleSceneCreate["footer-btn-container"]}>
            <Button
              htmlType="submit"
              className={styleSceneCreate["submit-btn"]}
              loading={isLoadingCreate || isLoadingUpdate}
              disabled={isLoadingCreate || isLoadingUpdate}
            >
              {!(isLoadingCreate || isLoadingUpdate) && (
                <FontAwesomeIcon icon={faCheck} />
              )}
              Submit
            </Button>
            <button className={styleSceneCreate["preview-btn"]}>
              <FontAwesomeIcon icon={faEye} /> Preview
            </button>
          </div>
        </Form>
      </section>
    </main>
  );
};

export default SceneCreate;